import { ILogger } from "@tdp/common/telemetry";
import { IAppValidationDetailsFetchResult, IAppValidationCountFetchResult, IAppValidationListFetchResult, IAppValidationReader, IAppPreValidationFetchResult, IAppValidationExportFetchResult } from "../abstractions/IAppValidationReader";
import { parseApiResponse } from "@tdp/core/util/parseApiResponse";
import { z } from "zod";
import { AppValidationList, AppValidationItem } from "../abstractions/IAppValidation"
import { AppPreValidation } from "../abstractions/IAppPreValidation";
import { IAppValidationWriter, INewAppValidationFetchResult } from "../abstractions/IAppValidationWriter";
import { AppValidationNewRequestSuccess, IAppValidationNewRequest, IAppValidationNewRequestSuccess } from "../abstractions/IAppValidationCreate";
import { ApiFetchStatus } from "@tdp/common/constants";

const appValidationApi = {
  getTotalValidationsByAppId: (appId: string) => {
    return `/v1.0/appvalidations/appdefinitions/${appId}/count`;
  },
  getValidationsByAppId: (appId: string, pageSize: number = 10) => {
    return `/v1.0/appvalidations/appdefinitions/${appId}?pageSize=${pageSize}`;
  },
  getValidationDetails: (validationId: string) => {
    return `/v1.0/appvalidations/${validationId}`;
  },
  createValidation: () => {
    return `/v1.0/appvalidations/appdefinition/validate`;
  },
  validationReportExport: (validationId: string) => {
    return `/v1.0/appvalidations/${validationId}/export`;
  },
  validationPackageExport: (validationId: string) => {
    return `/v1.0/appvalidations/${validationId}/apppackage`;
  },
  partnerCenterAppDefinitionValidation: (appId: string) => {
    return `/appdefinitions/${appId}/partnerCenterAppDefinitionValidation`;
  },
  partnerCenterAppPackageValidation: () => {
    return `/appdefinitions/partnerCenterAppPackageValidation`;
  }
};

export interface ITdpAppValidationService {
  logger: ILogger;
  apiFetch: typeof fetch;
}

export class TdpAppValidationService implements IAppValidationReader, IAppValidationWriter {
  private logger: ILogger;
  private apiFetch: typeof fetch;

  constructor({ apiFetch, logger }: ITdpAppValidationService) {
    this.apiFetch = apiFetch;
    this.logger = logger;
  }

  async getCount(appId: string): Promise<IAppValidationCountFetchResult> {
    const response = await this.apiFetch(appValidationApi.getTotalValidationsByAppId(appId));
    const parsedResponse = await parseApiResponse({
      response: response,
      errorMessage: "Error getting app validations count",
      logger: this.logger,
      zodType: z.object({ totalCount: z.number() }),
    });
    return {
      type: parsedResponse.type,
      ...(parsedResponse.errorType && { errorType: parsedResponse.errorType }),
      response: parsedResponse?.data?.totalCount || 0,
      ...(parsedResponse.errorMessage && { errorMessage: parsedResponse.errorMessage }),
    }
  }

  async getValidationDetails(validationId: string): Promise<IAppValidationDetailsFetchResult> {
    const response = await this.apiFetch(appValidationApi.getValidationDetails(validationId));
    const parsedResponse = await parseApiResponse({
      response: response,
      errorMessage: "Error getting app validation details",
      logger: this.logger,
      zodType: AppValidationItem,
    });
    return {
      type: parsedResponse.type,
      ...(parsedResponse.errorType && { errorType: parsedResponse.errorType }),
      ...(parsedResponse.data && { response: parsedResponse.data }),
      ...(parsedResponse.errorMessage && { errorMessage: parsedResponse.errorMessage }),
    }
  }

  async getValidationsList(appId: string, pageSize?: number, continuationToken?: string): Promise<IAppValidationListFetchResult> {
    const response = await this.apiFetch(appValidationApi.getValidationsByAppId(appId, pageSize), {
      method: 'GET',
      headers: continuationToken ? {
        "X-Continuation-Token": continuationToken
      } : {},
    });
    const parsedResponse = await parseApiResponse({
      response: response,
      errorMessage: "Error getting app validation list",
      logger: this.logger,
      zodType: AppValidationList,
    });
    return {
      type: parsedResponse.type,
      ...(parsedResponse.errorType && { errorType: parsedResponse.errorType }),
      ...(parsedResponse.data && { response: parsedResponse.data }),
      ...(parsedResponse.errorMessage && { errorMessage: parsedResponse.errorMessage }),
    }
  }

  async preValidateAppDefinition(appId: string): Promise<IAppPreValidationFetchResult> {
    const response = await this.apiFetch(appValidationApi.partnerCenterAppDefinitionValidation(appId), {
      method: 'GET',
    });
    const parsedResponse = await parseApiResponse({
      response: response,
      errorMessage: `Error in pre validating app definition ${appId}`,
      logger: this.logger,
      zodType: AppPreValidation,
    });
    return {
      type: parsedResponse.type,
      ...(parsedResponse.errorType && { errorType: parsedResponse.errorType }),
      ...(parsedResponse.data && { response: parsedResponse.data }),
      ...(parsedResponse.errorMessage && { errorMessage: parsedResponse.errorMessage }),
    }
  }

  async preValidateAppPackage(appPackage: File): Promise<IAppPreValidationFetchResult> {
    const response = await this.apiFetch(appValidationApi.partnerCenterAppPackageValidation(), {
      method: 'POST',
      headers: {
        "Content-Type": "application/zip;"
      },
      body: appPackage,
    });
    const parsedResponse = await parseApiResponse({
      response: response,
      errorMessage: `Error in pre validating app package`,
      logger: this.logger,
      zodType: AppPreValidation,
    });
    return {
      type: parsedResponse.type,
      ...(parsedResponse.errorType && { errorType: parsedResponse.errorType }),
      ...(parsedResponse.data && { response: parsedResponse.data }),
      ...(parsedResponse.errorMessage && { errorMessage: parsedResponse.errorMessage }),
    }
  }

  async newRequest(validaiton: IAppValidationNewRequest): Promise<INewAppValidationFetchResult> {
    const response = await this.apiFetch(appValidationApi.createValidation(), {
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(validaiton),
    });
    const parsedResponse = await parseApiResponse({
      response: response,
      errorMessage: `Error in creating app validation`,
      logger: this.logger,
      zodType: AppValidationNewRequestSuccess,
    });
    if (parsedResponse.type === ApiFetchStatus.SUCCESS) {
      return {
        type: ApiFetchStatus.SUCCESS,
        response: parsedResponse.data as IAppValidationNewRequestSuccess,
      }
    }
    else {
      return {
        type: ApiFetchStatus.ERROR,
        ...(parsedResponse.errorType && { errorType: parsedResponse.errorType }),
        ...(parsedResponse.errorMessage && { errorMessage: parsedResponse.errorMessage }),
      }
    }
  }

  async exportDetails(validationId: string, ext: 'csv' | 'zip'): Promise<IAppValidationExportFetchResult> {
    const api = ext === 'csv'
      ? appValidationApi.validationReportExport(validationId)
      : appValidationApi.validationPackageExport(validationId)
    const response = await this.apiFetch(api, {
      method: 'GET',
    });

    if (ext === 'zip') {
      const parsedResponse = await parseApiResponse({
        response: response,
        errorMessage: `Error inexporting validation details`,
        logger: this.logger,
        zodType: z.string(),
      });
      return {
        type: parsedResponse.type,
        ...(parsedResponse.errorType && { errorType: parsedResponse.errorType }),
        ...(parsedResponse.data && { response: parsedResponse.data }),
        ...(parsedResponse.errorMessage && { errorMessage: parsedResponse.errorMessage }),
      }
    }

    else {
      const responseObject = (await response.body) as ReadableStream;
      const reader = responseObject.getReader();
      const decoder = new TextDecoder("utf-8");
      const { done, value } = await reader.read();
      const text = decoder.decode(value, { stream: true });
      if (text) {
        return {
          type: ApiFetchStatus.SUCCESS,
          response: text,
        }
      }
      else {
        return {
          type: ApiFetchStatus.ERROR,
          errorMessage: `Error in exporting report`,
        }
      }
    }
  }
}
